import { memo, useMemo } from "react";
import { TreeSelect, DropdownSkeleton } from "@atlas-int/ui";
import {
  buildProductTree,
  getInitialExpandedProductIds,
  productSearchFunction,
  getLabelForProduct,
  ProductItem,
} from "./ProductDropdownUtils";
import ProductDropdownItemMUI from "./ProductDropdownItemMUI";

interface ProductDropdownProps {
  /** Flat list of product items to display */
  products: ProductItem[];
  /** Currently selected product ID */
  value: string | undefined;
  /** Called when the user selects a product */
  onChange: (productId: string) => void;
  /** Product class label (e.g. "HS92", "SITC") for display */
  productClassLabel: string;
  /** ID of the "All Products" item, used for sticky header and label formatting */
  allProductsId?: string;
  /** Loading state */
  loading?: boolean;
  /** Error state */
  error?: boolean;
}

const ProductDropdown = ({
  products,
  value,
  onChange,
  productClassLabel,
  allProductsId,
  loading = false,
  error = false,
}: ProductDropdownProps) => {
  // Find selected product
  const selectedProduct = useMemo(() => {
    return products.find((item) => item.id === value);
  }, [products, value]);

  // Get initially expanded IDs
  const initialExpandedIds = useMemo(() => {
    return getInitialExpandedProductIds(products, selectedProduct, allProductsId);
  }, [products, selectedProduct, allProductsId]);

  // Select handler
  const handleSelect = (item: ProductItem) => {
    onChange(item.id);
  };

  if (loading || products.length === 0) {
    return <DropdownSkeleton />;
  }

  if (error) {
    return <DropdownSkeleton />;
  }

  return (
    <TreeSelect
      items={products}
      buildTree={buildProductTree}
      value={selectedProduct}
      onChange={handleSelect}
      placeholder="Please select a product"
      getLabel={(item) => getLabelForProduct(item, productClassLabel, allProductsId)}
      searchFilter={productSearchFunction}
      renderItem={(props) => (
        <ProductDropdownItemMUI
          {...props}
          productClass={productClassLabel}
          allProductsId={allProductsId}
        />
      )}
      estimatedItemHeight={34}
      defaultExpandedIds={initialExpandedIds}
      maxHeight="300px"
      minWidth="350px"
    />
  );
};

export default memo(ProductDropdown);
